import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { InstallButton } from "@/components/InstallPWA";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate(location.state?.from || "/", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="WELKOM TERUG" subtitle="Log in om te kopen en verkopen.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {error && <p className="rounded-lg border border-[#EF4444]/40 bg-[#EF4444]/10 p-3 text-sm text-[#EF4444]" data-testid="login-error">{error}</p>}
        <Input label="E-mail" type="email" value={email} onChange={setEmail} testid="login-email" />
        <Input label="Wachtwoord" type="password" value={password} onChange={setPassword} testid="login-password" />
        <div className="text-right">
          <Link to="/wachtwoord-vergeten" data-testid="forgot-password-link" className="text-sm font-semibold text-[#FF6A00] hover:underline">Wachtwoord vergeten?</Link>
        </div>
        <button type="submit" disabled={loading} data-testid="login-submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-[#FF6A00] py-3.5 font-bold text-[#111] transition-all hover:bg-[#FFB27A] active:scale-95 disabled:opacity-60">
          {loading && <Loader2 className="h-5 w-5 animate-spin" />} Inloggen
        </button>
      </form>
      <div className="mt-4 flex flex-col gap-3">
        <Link to="/registreren" data-testid="go-register" className="w-full rounded-full border border-[#333] py-3 text-center font-semibold text-[#F5F5F5] transition-colors hover:border-[#FF6A00] hover:text-[#FF6A00]">Maak account</Link>
        <InstallButton variant="ghost" className="w-full" />
      </div>
      <div className="mt-4 rounded-lg border border-[#333] bg-[#1c1c1c] p-3 text-center text-xs text-[#777]">
        Demo verkoper: <b className="text-[#A0A0A0]">mark@vrijmarkt.nl</b> / <b className="text-[#A0A0A0]">demo123</b>
      </div>
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="flex min-h-[calc(100vh-64px)] items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center"><Logo height={52} /></div>
        <div className="rounded-2xl border border-[#333] bg-[#1c1c1c] p-6 sm:p-8">
          <h1 className="font-display text-3xl text-[#F5F5F5]">{title}</h1>
          <p className="mb-6 mt-1 text-sm text-[#A0A0A0]">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export function Input({ label, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-semibold text-[#F5F5F5]">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        className="w-full rounded-lg border border-[#333] bg-[#2A2A2A] p-3 text-[#F5F5F5] outline-none placeholder:text-[#777] focus:border-[#FF6A00] focus:ring-1 focus:ring-[#FF6A00]"
      />
    </div>
  );
}
